import type { ReactNode } from 'react'

interface ChipProps {
  children: ReactNode
  /** Present ⇒ the chip is selectable and renders as a button. */
  onSelect?: () => void
  selected?: boolean
  icon?: ReactNode
}

/**
 * A chip: a small pill that is either a filter you can press or a label you
 * cannot.
 *
 * The two cases render different elements on purpose. A `<span>` styled to look
 * pressable is a lie the accessibility tree repeats — screen readers and
 * keyboard users get no affordance while sighted users get one. So: `<button
 * aria-pressed>` when `onSelect` is given, `<span>` when it is not.
 *
 * Selected state is ink-on-ground inverted rather than accent-filled: chips
 * appear in rows of six, and six coral pills is the opposite of "one hot accent
 * used sparingly".
 *
 * SUAP-0103.
 */
export function Chip({ children, onSelect, selected = false, icon }: ChipProps) {
  // The transition names its properties instead of using `transition-colors`,
  // which includes `outline-color`: with it, a keyboard user's focus ring fades
  // in from the previous colour over 150 ms, and on a *selected* chip the
  // previous colour is the chip's own fill — a ring that is invisible for the
  // first frames. Found by `check:a11y` measuring the ring immediately after Tab
  // (SUAP-0103). A focus indicator should not animate.
  const shared = 'inline-flex min-h-11 items-center gap-1.5 rounded-chip border px-3 text-sm'

  if (onSelect === undefined) {
    return (
      <span className={`${shared} border-hairline bg-surface text-ink-soft`}>
        {icon}
        {children}
      </span>
    )
  }

  return (
    <button
      type="button"
      aria-pressed={selected}
      onClick={onSelect}
      className={[
        shared,
        'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ink',
        'motion-safe:transition-[background-color,border-color,color]',
        selected ? 'border-ink bg-ink text-ground' : 'border-hairline bg-surface text-ink',
      ].join(' ')}
    >
      {icon}
      {children}
    </button>
  )
}
